import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { FileText, Sparkles, Loader2, ArrowLeft, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BizPlanGenerator = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    business_name: '',
    industry: '',
    business_description: '',
    target_market: '',
    products_services: '',
    startup_costs: '',
    funding_needed: '',
    timeline: '1 year',
    plan_type: 'basic',
  });

  const industries = [
    'Technology / Software',
    'E-commerce / Retail',
    'Food & Beverage',
    'Healthcare / Medical',
    'Professional Services',
    'Construction / Real Estate',
    'Manufacturing',
    'Education',
    'Fitness / Wellness',
    'Transportation / Logistics',
    'Entertainment / Media',
    'Finance / Fintech',
    'Agriculture',
    'Other',
  ];

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleGenerate = async () => {
    // Validate required fields
    if (!formData.business_name || !formData.industry || !formData.business_description || 
        !formData.target_market || !formData.products_services) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('careerboost_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/bizplan/generate`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Business plan generated!');
      navigate(`/bizplanai/plans/${response.data.plan_id}`);
    } catch (error) {
      console.error('Generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate plan');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (step === 1 && (!formData.business_name || !formData.industry)) {
      toast.error('Please fill in business name and industry');
      return;
    }
    if (step === 2 && (!formData.business_description || !formData.target_market)) {
      toast.error('Please fill in all required fields');
      return;
    }
    setStep(step + 1);
  };

  const prevStep = () => setStep(step - 1);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/bizplanai/dashboard')} data-testid="back-to-dashboard">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold text-lg text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  BizPlanAI
                </span>
              </div>
            </div>
            <div className="text-sm text-slate-500">
              Step {step} of 3
            </div>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-emerald-600 transition-all duration-300"
              style={{ width: `${(step / 3) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl mx-auto px-4 py-12">
        {step === 1 && (
          <Card data-testid="step-1">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                Let's start with the basics
              </CardTitle>
              <CardDescription>
                Tell us about your business idea
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Business Name *</Label>
                <Input
                  value={formData.business_name}
                  onChange={(e) => handleChange('business_name', e.target.value)}
                  placeholder="e.g., TechFlow Solutions"
                  data-testid="input-business-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Industry *</Label>
                <Select
                  value={formData.industry}
                  onValueChange={(value) => handleChange('industry', value)}
                >
                  <SelectTrigger data-testid="select-industry">
                    <SelectValue placeholder="Select your industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {industries.map((industry) => (
                      <SelectItem key={industry} value={industry}>
                        {industry}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Plan Type</Label>
                <Select
                  value={formData.plan_type}
                  onValueChange={(value) => handleChange('plan_type', value)}
                >
                  <SelectTrigger data-testid="select-plan-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Basic ($49) - Essential sections</SelectItem>
                    <SelectItem value="detailed">Detailed ($99) - Investor-ready</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={nextStep} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="next-step-1">
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card data-testid="step-2">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                Describe your business
              </CardTitle>
              <CardDescription>
                The more detail you provide, the better your plan will be
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Business Description *</Label>
                <Textarea
                  value={formData.business_description}
                  onChange={(e) => handleChange('business_description', e.target.value)}
                  placeholder="Describe what your business does, your mission, and what makes it unique..."
                  rows={4}
                  data-testid="input-description"
                />
              </div>
              <div className="space-y-2">
                <Label>Target Market *</Label>
                <Textarea
                  value={formData.target_market}
                  onChange={(e) => handleChange('target_market', e.target.value)}
                  placeholder="Who are your ideal customers? Be specific about demographics, location, needs..."
                  rows={3}
                  data-testid="input-target-market"
                />
              </div>
              <div className="space-y-2">
                <Label>Products / Services *</Label>
                <Textarea
                  value={formData.products_services}
                  onChange={(e) => handleChange('products_services', e.target.value)}
                  placeholder="List your main products or services and their pricing..."
                  rows={3}
                  data-testid="input-products"
                />
              </div>
              <div className="flex gap-4">
                <Button variant="outline" onClick={prevStep} className="flex-1" data-testid="prev-step-2">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button onClick={nextStep} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="next-step-2">
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 3 && (
          <Card data-testid="step-3">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                Financial Details (Optional)
              </CardTitle>
              <CardDescription>
                Help us create accurate financial projections
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Estimated Startup Costs</Label>
                <Input
                  value={formData.startup_costs}
                  onChange={(e) => handleChange('startup_costs', e.target.value)}
                  placeholder="e.g., $50,000"
                  data-testid="input-startup-costs"
                />
              </div>
              <div className="space-y-2">
                <Label>Funding Needed</Label>
                <Input
                  value={formData.funding_needed}
                  onChange={(e) => handleChange('funding_needed', e.target.value)}
                  placeholder="e.g., $100,000"
                  data-testid="input-funding"
                />
              </div>
              <div className="space-y-2">
                <Label>Timeline to Launch</Label>
                <Select
                  value={formData.timeline}
                  onValueChange={(value) => handleChange('timeline', value)}
                >
                  <SelectTrigger data-testid="select-timeline">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3 months">3 months</SelectItem>
                    <SelectItem value="6 months">6 months</SelectItem>
                    <SelectItem value="1 year">1 year</SelectItem>
                    <SelectItem value="2 years">2 years</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Summary */}
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <h4 className="font-medium text-emerald-900 mb-2">Plan Summary</h4>
                <div className="text-sm text-emerald-700 space-y-1">
                  <p><strong>Business:</strong> {formData.business_name}</p>
                  <p><strong>Industry:</strong> {formData.industry}</p>
                  <p><strong>Plan Type:</strong> {formData.plan_type === 'basic' ? 'Basic ($49)' : 'Detailed ($99)'}</p>
                </div>
              </div>

              <div className="flex gap-4">
                <Button variant="outline" onClick={prevStep} className="flex-1" data-testid="prev-step-3">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button 
                  onClick={handleGenerate} 
                  disabled={loading}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700" 
                  data-testid="generate-plan-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Plan
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default BizPlanGenerator;
